 
import React from "react";
import { ProductCard } from "./ProductCard";
import { Gift, Product, Promo } from "../../../types/types";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";

interface CatalogProps {
  products: Product[];
  isLoading?: boolean;
  searchQuery: string;
  categoryFilter: string;
  total?: number;
  pages?: number;
  pageNo?: number;
  limit?: number;
  compact?: boolean;

  onEditProduct?: (product: Product) => void;
  onViewProduct?: (product: Product) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onDeletePromo: (id: string) => void;
  onDeleteGift: (id: string) => void;
  onAddProductClick: () => void;
  onPageChange?: (page: number) => void;

  promos?: Promo[];
  gifts?: Gift[];
  onEditPromo?: (promo: Promo) => void;
  onEditGift?: (gift: Gift) => void;
  onTogglePromoStatus?: (id: string, currentStatus: string) => void;
  onToggleGiftStatus?: (id: string, currentStatus: string) => void;
}

export const Catalog: React.FC<CatalogProps> = ({
  products, 
  isLoading,
  searchQuery,
  categoryFilter,
  total = 0,
  pages = 1,
  pageNo = 1,
  limit = 50,
  compact = false,
  onEditProduct,
  onViewProduct,
  onToggleStatus,
  onPageChange,
  promos = [],
  gifts = [],
  onEditPromo,
  onEditGift,
  onTogglePromoStatus,
  onToggleGiftStatus,
}) => {
  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-3"} gap-5`}>
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <div
            key={n}
            className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 animate-pulse shadow-sm"
          >
            <div className="flex gap-4">
              <div className="w-20 h-20 bg-slate-200 rounded-xl shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-200 rounded w-1/3" />
                <div className="h-5 bg-slate-200 rounded w-2/3" />
                <div className="h-3 bg-slate-200 rounded w-full" />
              </div>
            </div>
            <div className="h-20 bg-slate-100 rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  const filteredProducts = products.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory =
      categoryFilter === "All" || p.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  return (
    <div className="space-y-6">
      {/* Product List */}
      {filteredProducts.length === 0 ? (
        <NoDataFound mainClassName="" />
      ) : (
        <>
          <div className={`grid grid-cols-2 ${compact ? "" : "md:grid-cols-3"} gap-5`}>
            {filteredProducts.map((product) => (
               <ProductCard
                 key={product.id}
                 product={product}
                 compact={compact}
                 onEdit={onEditProduct}
                 onView={onViewProduct}
                 onToggleStatus={onToggleStatus}
                 promos={promos}
                 gifts={gifts}
                 onEditPromo={onEditPromo}
                 onEditGift={onEditGift}
                 onTogglePromoStatus={onTogglePromoStatus}
                 onToggleGiftStatus={onToggleGiftStatus}
               />
            ))}
          </div>

          {pages > 1 && (
            <div className="flex justify-end pt-4">
              <Pagination
                currentPage={pageNo}
                totalPages={pages}
                totalItems={total}
                fetchData={onPageChange}
              />
            </div>
          )}
        </>
      )}
 
    </div>
  );
};